import { Button, DownloadSVG, PlusSVG } from "@newsela/angelou";
import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";
import { useMutation } from "@tanstack/react-query";

import api, { ApiCatalog } from "@/api";
import AIButton from "@/components/AIComponents/AiButton";
import { useExportablesContext } from "@/components/AIComponents/ExportablesModal/ExportablesContextProvider/useExportablesContext";
import { useGetUser } from "@/users/v3";
import buildAssignedGraphicOrganizerPayload from "@/view/utils/buildAssignedGraphicOrganizerPayload/buildAssignedGraphicOrganizerPayload";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import {
  hideOnMobileCss,
  hideOnTabletAndUpCss,
  teacherActionsWrapperCss,
  warningTextCss,
} from "./ExportablesModalFooter.styles";
import { getDownloadHandler } from "./utils";

const ButtonWithTrackEvent = withTrackEvent(Button);
const AIButtonWithTrackEvent = withTrackEvent(AIButton);

const TeacherActions = () => {
  const {
    content,
    formData,
    handleModalClose,
    isInAssignGraphicOrganizersExperiment,
    isAssignableGraphicOrganizersActivityChecked,
    selectedExportable,
    setShowAssignmentWarning,
    setAssignedTemplates,
    templatesToAssign,
    renderOnMobile,
  } = useExportablesContext();
  const { data: user } = useGetUser();
  const { updatedAssignment } = useAssignmentReducerContext();
  const { mutateAsync: postGraphicOrganizers } = useMutation({
    mutationFn: async (payload: any) => {
      await api().post(ApiCatalog.graphicOrganizer.endpoint, payload);
    },
  });

  const label = selectedExportable.label || selectedExportable.title;
  const exportableLabel =
    label?.toLowerCase().replace(/\s+/g, "-") || "exportable";
  const downloadButtonQaSelector = `download-${exportableLabel}-pdf`;
  const addToAssignmentButtonQaSelector = `add-to-assignment-${exportableLabel}`;

  const handleAddToAssignment = async () => {
    setShowAssignmentWarning(templatesToAssign.length === 0);

    if (
      templatesToAssign.length === 0 ||
      !isAssignableGraphicOrganizersActivityChecked
    )
      return;

    setAssignedTemplates(templatesToAssign);

    try {
      const payload = buildAssignedGraphicOrganizerPayload({
        templates: templatesToAssign,
        formData,
        updatedAssignmentId: updatedAssignment?.content_id,
        userId: user?.unique_id,
        contentId: content.id,
      });

      await postGraphicOrganizers(payload);

      await handleModalClose();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDownload = getDownloadHandler({
    formData: formData?.[selectedExportable.seti_identifier] || null,
    selectedExportable,
  });

  const downloadButtonProps = {
    flavor: Button.flavors.secondary,
    onClick: handleDownload,
    icon: {
      SvgComponent: DownloadSVG,
      alignment: Button.iconAlignments.right,
    },
    dataProps: { "data-qa-selector": downloadButtonQaSelector },
    objectType: objectTypes.generatedTextAsset,
    actionPrefix: actionPrefixes.initiate,
    actionName: actionNames.download,
    streamProperties: {
      description: "Download exportable PDF",
    },
  };

  return (
    <>
      <p
        css={[warningTextCss, !renderOnMobile && hideOnMobileCss]}
        data-qa-selector="ai-warning-text"
      >
        AI can make mistakes. Be sure to check AI-generated content before
        using.
      </p>
      {isInAssignGraphicOrganizersExperiment ? (
        <div
          css={[teacherActionsWrapperCss, !renderOnMobile && hideOnMobileCss]}
        >
          <ButtonWithTrackEvent {...downloadButtonProps} isFullWidth>
            <span css={hideOnTabletAndUpCss}>Download</span>
            <span css={hideOnMobileCss}>Download as a PDF</span>
          </ButtonWithTrackEvent>
          <AIButtonWithTrackEvent
            onClick={handleAddToAssignment}
            icon={{ SvgComponent: PlusSVG }}
            dataProps={{ "data-qa-selector": addToAssignmentButtonQaSelector }}
            objectType={objectTypes.graphicOrganizer}
            actionPrefix={actionPrefixes.initiate}
            actionName={actionNames.add}
            streamProperties={{
              description: `Add ${label.toLowerCase()} exportable to assignment`,
            }}
            isFullWidth
          >
            <span css={hideOnTabletAndUpCss}>Add</span>
            <span css={hideOnMobileCss}>
              Add to assignment ({templatesToAssign.length})
            </span>
          </AIButtonWithTrackEvent>
        </div>
      ) : (
        <div css={!renderOnMobile && hideOnMobileCss}>
          <AIButtonWithTrackEvent {...downloadButtonProps}>
            Download as a PDF
          </AIButtonWithTrackEvent>
        </div>
      )}
    </>
  );
};

export default TeacherActions;
